<script setup lang="ts">
import { onMounted, ref } from "vue";
import { KeyRound, Pencil, Plus, RefreshCw, ShieldCheck, UserRound, X } from "lucide-vue-next";
import { api } from "../services/api";
import type { AdminOperationLog, AdminRole, AdminUser } from "../types";

const users = ref<AdminUser[]>([]);
const logs = ref<AdminOperationLog[]>([]);
const loading = ref(false);
const saving = ref(false);
const error = ref("");
const formError = ref("");
const modalOpen = ref(false);
const editingId = ref<string>();
const username = ref("");
const displayName = ref("");
const password = ref("");
const role = ref<AdminRole>("ADMIN");
const isEnabled = ref(true);

async function load(): Promise<void> {
  loading.value = true;
  error.value = "";
  try {
    [users.value, logs.value] = await Promise.all([api.adminUsers(), api.adminOperationLogs(40)]);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "用户数据加载失败";
  } finally {
    loading.value = false;
  }
}

function openCreate(): void {
  editingId.value = undefined;
  username.value = "";
  displayName.value = "";
  password.value = "";
  role.value = "ADMIN";
  isEnabled.value = true;
  formError.value = "";
  modalOpen.value = true;
}

function openEdit(user: AdminUser): void {
  editingId.value = user.id;
  username.value = user.username;
  displayName.value = user.displayName;
  password.value = "";
  role.value = user.role;
  isEnabled.value = user.isEnabled;
  formError.value = "";
  modalOpen.value = true;
}

function validateForm(): string {
  if (!displayName.value.trim()) return "请输入显示名称";
  if (displayName.value.trim().length > 80) return "显示名称不能超过 80 个字符";
  if (!editingId.value) {
    if (!/^[a-z0-9][a-z0-9_.-]{2,39}$/i.test(username.value.trim())) {
      return "用户名需为 3-40 位字母、数字、点、下划线或连字符";
    }
    if (password.value.length < 10) return "初始密码至少需要 10 位";
  }
  if (password.value.length > 128) return "密码不能超过 128 位";
  if (editingId.value && password.value && password.value.length < 10) return "新密码至少需要 10 位";
  return "";
}

async function save(): Promise<void> {
  formError.value = validateForm();
  if (formError.value) return;
  saving.value = true;
  formError.value = "";
  try {
    if (editingId.value) {
      await api.updateAdminUser(editingId.value, {
        displayName: displayName.value.trim(), role: role.value, isEnabled: isEnabled.value,
      });
      if (password.value) await api.resetAdminPassword(editingId.value, password.value);
    } else {
      await api.createAdminUser({
        username: username.value.trim(), displayName: displayName.value.trim(), password: password.value, role: role.value,
      });
    }
    modalOpen.value = false;
    await load();
  } catch (reason) {
    formError.value = reason instanceof Error ? reason.message : "用户保存失败";
  } finally {
    saving.value = false;
  }
}

function formatTime(value?: string | null): string {
  return value ? new Intl.DateTimeFormat("zh-CN", { dateStyle: "short", timeStyle: "medium" }).format(new Date(value)) : "从未";
}
</script>

<template>
  <div class="view-stack">
    <section class="surface-section">
      <div class="section-header">
        <div><span class="eyebrow">权限</span><h2>管理员用户</h2></div>
        <div class="section-actions">
          <button class="icon-button compact" type="button" title="刷新" :disabled="loading" @click="load"><RefreshCw :size="16" /></button>
          <button class="button primary" type="button" @click="openCreate"><Plus :size="16" />新增用户</button>
        </div>
      </div>
      <div v-if="error" class="empty-state error-state">{{ error }}</div>
      <div v-else-if="!users.length" class="empty-state">暂无管理员账户</div>
      <div v-else class="admin-user-list">
        <button v-for="user in users" :key="user.id" type="button" class="admin-user-row" @click="openEdit(user)">
          <span class="user-avatar"><ShieldCheck v-if="user.role === 'SUPER_ADMIN'" :size="18" /><UserRound v-else :size="18" /></span>
          <span class="user-primary"><strong>{{ user.displayName }}</strong><small>{{ user.username }}</small></span>
          <span class="role-label">{{ user.role === "SUPER_ADMIN" ? "超级管理员" : "管理员" }}</span>
          <span :class="['status-pill', user.isEnabled ? 'ok' : 'muted']">{{ user.isEnabled ? "启用" : "停用" }}</span>
          <span class="user-login">{{ formatTime(user.lastLoginAt) }}</span>
          <Pencil :size="15" />
        </button>
      </div>
    </section>

    <section class="surface-section">
      <div class="section-header"><div><span class="eyebrow">审计</span><h2>管理操作</h2></div></div>
      <div v-if="!logs.length" class="empty-state">暂无操作记录</div>
      <div v-else class="operation-log-list">
        <div v-for="item in logs" :key="item.id" class="operation-log-row">
          <span :class="['status-dot', item.statusCode < 400 ? 'ok' : 'bad']" />
          <strong>{{ item.actor }}</strong><code>{{ item.action }}</code><span>{{ item.resource }}</span>
          <small>{{ formatTime(item.createdAt) }}</small>
        </div>
      </div>
    </section>

    <div v-if="modalOpen" class="modal-backdrop" @click.self="modalOpen = false">
      <section class="modal-panel" role="dialog" aria-modal="true" aria-labelledby="admin-user-title">
        <div class="modal-header">
          <div><span class="eyebrow">账户</span><h2 id="admin-user-title">{{ editingId ? "编辑管理员" : "新增管理员" }}</h2></div>
          <button class="icon-button" type="button" title="关闭" @click="modalOpen = false"><X :size="18" /></button>
        </div>
        <form class="form-stack" @submit.prevent="save">
          <label><span>用户名</span><input v-model="username" :disabled="Boolean(editingId)" autocomplete="off" maxlength="40" @input="formError = ''" /></label>
          <label><span>显示名称</span><input v-model="displayName" autocomplete="off" maxlength="80" @input="formError = ''" /></label>
          <label><span>{{ editingId ? "重置密码（留空不修改）" : "初始密码" }}</span><input v-model="password" type="password" autocomplete="new-password" maxlength="128" @input="formError = ''" /></label>
          <label><span>角色</span><select v-model="role"><option value="ADMIN">管理员</option><option value="SUPER_ADMIN">超级管理员</option></select></label>
          <label v-if="editingId" class="checkbox-row"><input v-model="isEnabled" type="checkbox" /><span>账户启用</span></label>
          <div v-if="formError" class="modal-form-error" role="alert">{{ formError }}</div>
          <div class="modal-actions">
            <button class="button secondary" type="button" @click="modalOpen = false">取消</button>
            <button class="button primary" type="submit" :disabled="saving"><KeyRound :size="16" />{{ saving ? "保存中" : "保存" }}</button>
          </div>
        </form>
      </section>
    </div>
  </div>
</template>
